import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { Palette, Save, RotateCcw } from 'lucide-react';

const SchoolColors = () => {
  const { user } = useAuth();
  const { api } = useApi();
  const [colors, setColors] = useState({
    school1: '#ff0000',
    school2: '#0000ff', 
    school3: '#ffff00'
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchSchoolColors = useCallback(async () => {
    if (!user) return;

    try {
      const response = await api.get(`/users/${user.id}/school-colors`);
      if (response.data.colors) {
        setColors(response.data.colors);
      }
    } catch (error) {
      console.error('Failed to fetch school colors:', error);
    }
  }, [api, user]);

  useEffect(() => {
    fetchSchoolColors();
  }, [fetchSchoolColors]);

  const handleColorChange = (colorKey, value) => {
    setColors(prev => ({
      ...prev,
      [colorKey]: value
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await api.put(`/users/${user.id}/school-colors`, { colors });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save school colors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setColors({
      school1: '#ff0000',
      school2: '#0000ff',
      school3: '#ffff00'
    });
    setSaved(false);
  };

  return (
    <div className="card">
      <div className="flex items-center mb-6">
        <Palette className="w-6 h-6 mr-3 text-black" />
        <h2 className="text-xl font-bold text-black">School Colors</h2>
      </div>

      <div className="space-y-6">
        <p className="text-gray-700">
          Customize your school colors to match your band's identity. These colors will be used throughout your dashboard and video presentations.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* School Color 1 */}
          <div className="space-y-3">
            <label className="form-label text-black">Primary School Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={colors.school1}
                onChange={(e) => handleColorChange('school1', e.target.value)}
                className="w-12 h-12 border-2 border-black cursor-pointer"
              />
              <input
                type="text"
                value={colors.school1}
                onChange={(e) => handleColorChange('school1', e.target.value)}
                className="input flex-1 bg-sky text-black border-black"
                placeholder="#ff0000"
              />
            </div>
            <div className="h-8 border-2 border-black" style={{ backgroundColor: colors.school1 }}></div>
          </div>

          {/* School Color 2 */}
          <div className="space-y-3">
            <label className="form-label text-black">Secondary School Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={colors.school2}
                onChange={(e) => handleColorChange('school2', e.target.value)}
                className="w-12 h-12 border-2 border-black cursor-pointer"
              />
              <input
                type="text"
                value={colors.school2}
                onChange={(e) => handleColorChange('school2', e.target.value)}
                className="input flex-1 bg-sky text-black border-black"
                placeholder="#0000ff"
              />
            </div>
            <div className="h-8 border-2 border-black" style={{ backgroundColor: colors.school2 }}></div>
          </div>

          {/* School Color 3 */}
          <div className="space-y-3">
            <label className="form-label text-black">Accent School Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={colors.school3}
                onChange={(e) => handleColorChange('school3', e.target.value)}
                className="w-12 h-12 border-2 border-black cursor-pointer"
              />
              <input
                type="text"
                value={colors.school3}
                onChange={(e) => handleColorChange('school3', e.target.value)}
                className="input flex-1 bg-sky text-black border-black"
                placeholder="#ffff00"
              />
            </div>
            <div className="h-8 border-2 border-black" style={{ backgroundColor: colors.school3 }}></div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="border-2 border-black p-6 bg-goldie">
          <h3 className="text-lg font-semibold text-black mb-4">Preview</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button 
              className="btn px-4 py-2"
              style={{ 
                backgroundColor: colors.school1,
                borderColor: colors.school1,
                color: getContrastColor(colors.school1)
              }}
            >
              Primary Button
            </button>
            <button 
              className="btn px-4 py-2"
              style={{ 
                backgroundColor: colors.school2,
                borderColor: colors.school2,
                color: getContrastColor(colors.school2)
              }}
            >
              Secondary Button
            </button>
            <button 
              className="btn px-4 py-2"
              style={{ 
                backgroundColor: colors.school3,
                borderColor: colors.school3,
                color: getContrastColor(colors.school3)
              }}
            >
              Accent Button
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-4">
          <button
            onClick={handleSave}
            disabled={loading}
            className="btn-primary flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save Colors'}
          </button>
          <button
            onClick={handleReset}
            className="btn-outline flex items-center"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Defaults
          </button>
        </div>

        {saved && (
          <div className="text-success-500 text-sm font-medium">
            School colors saved successfully!
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to determine text color based on background
const getContrastColor = (hexColor) => {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#ffffff';
};

export default SchoolColors;
